import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDialog } from 'components/core/Dialog/common/DialogContext';
import { useProjectId } from 'store/project';
import { useInspectionStore } from '../store';
import { CommonTranslationKeys, DialogTranslationKeys } from 'intl/keys';
import { formatTranslation, FormattedMessage } from 'intl';
import { translationKeyToString } from 'intl/types/translationKey';

export interface UseTemplateActionsOptions {
  onAfterArchive?: (templateId: string) => void;
  onAfterRestore?: (templateId: string) => void;
  onAfterRemove?: (templateId: string) => void;
}

export interface UseTemplateActionsResult {
  archiveTemplate: (templateId: string) => Promise<void>;
  restoreTemplate: (templateId: string) => Promise<void>;
  removeTemplate: (templateId: string, templateSummary: string) => Promise<void>;
  isRemoving: boolean;
}

export function useTemplateActions(
  options: UseTemplateActionsOptions = {}
): UseTemplateActionsResult {
  const { onAfterArchive, onAfterRestore, onAfterRemove } = options;
  const intl = useIntl();
  const openDialog = useDialog();
  const projectId = useProjectId();
  const archiveTemplateApi = useInspectionStore.use.archiveTemplate();
  const restoreTemplateApi = useInspectionStore.use.restoreTemplate();
  const removeInspectionTemplateApi =
    useInspectionStore.use.removeInspectionTemplate();
  const [isRemoving, setIsRemoving] = useState(false);

  const archiveTemplate = useCallback(
    async (templateId: string): Promise<void> => {
      await openDialog({
        catchOnCancel: true,
        rejectOnOffline: true,
        title: (
          <FormattedMessage id={DialogTranslationKeys.confirmationArchiveTemplateTitle} />
        ),
        description: (
          <FormattedMessage
            id={DialogTranslationKeys.confirmationArchiveTemplateDescription}
          />
        ),
        customControllerLabels: [
          formatTranslation(intl, CommonTranslationKeys.cancel),
          formatTranslation(intl, CommonTranslationKeys.archive),
        ],
      });

      await archiveTemplateApi(templateId, projectId);
      onAfterArchive?.(templateId);
    },
    [intl, openDialog, projectId, archiveTemplateApi, onAfterArchive]
  );

  const restoreTemplate = useCallback(
    async (templateId: string): Promise<void> => {
      await openDialog({
        catchOnCancel: true,
        rejectOnOffline: true,
        title: (
          <FormattedMessage id={DialogTranslationKeys.confirmationRestoreTemplateTitle} />
        ),
        description: (
          <FormattedMessage
            id={DialogTranslationKeys.confirmationRestoreTemplateDescription}
          />
        ),
        customControllerLabels: [
          formatTranslation(intl, CommonTranslationKeys.cancel),
          formatTranslation(intl, CommonTranslationKeys.restore),
        ],
      });

      await restoreTemplateApi(templateId, projectId);
      onAfterRestore?.(templateId);
    },
    [intl, openDialog, projectId, restoreTemplateApi, onAfterRestore]
  );

  const removeTemplate = useCallback(
    async (templateId: string, templateSummary: string): Promise<void> => {
      await openDialog({
        catchOnCancel: true,
        rejectOnOffline: true,
        title: (
          <FormattedMessage
            id={DialogTranslationKeys.removeInspectionTemplateDialogTitle}
          />
        ),
        description: (
          <FormattedMessage
            id={DialogTranslationKeys.removeInspectionTemplateDialogDescription}
            values={{
              templateTitle: (
                <strong style={{ color: 'crimson' }}>{templateSummary}</strong>
              ),
            }}
          />
        ),
        customControllerLabels: [
          translationKeyToString(CommonTranslationKeys.cancel),
          translationKeyToString(CommonTranslationKeys.delete),
        ],
      });

      setIsRemoving(true);
      try {
        await removeInspectionTemplateApi(templateId, projectId);
        onAfterRemove?.(templateId);
      } finally {
        setIsRemoving(false);
      }
    },
    [openDialog, removeInspectionTemplateApi, projectId, onAfterRemove]
  );

  return {
    archiveTemplate,
    restoreTemplate,
    removeTemplate,
    isRemoving,
  };
}
